/**
 * Fires `onIdle` after `timeoutMs` without user input.
 *
 * Uses capture-phase listeners on `window` so a screen that calls
 * stopPropagation() on its own handlers can't accidentally freeze the kiosk on
 * an interactive page forever.
 */
export class IdleTimer {
  private handle: number | undefined
  private running = false
  private paused = false

  private readonly kick = (): void => {
    if (!this.running || this.paused) return
    this.clear()
    this.handle = window.setTimeout(this.onIdle, this.timeoutMs)
  }

  constructor(
    private readonly timeoutMs: number,
    private readonly onIdle: () => void,
  ) {}

  start(): void {
    if (this.running) return
    this.running = true
    for (const event of IdleTimer.EVENTS) {
      window.addEventListener(event, this.kick, { capture: true, passive: true })
    }
    this.kick()
  }

  stop(): void {
    if (!this.running) return
    this.running = false
    for (const event of IdleTimer.EVENTS) {
      window.removeEventListener(event, this.kick, { capture: true })
    }
    this.clear()
  }

  /**
   * Suppress the timer while the attract loop is already on screen.
   * Without the `paused` flag the next stray touch would re-arm it.
   */
  pause(): void {
    this.paused = true
    this.clear()
  }

  resume(): void {
    this.paused = false
    this.kick()
  }

  private clear(): void {
    if (this.handle !== undefined) {
      window.clearTimeout(this.handle)
      this.handle = undefined
    }
  }

  private static readonly EVENTS = [
    'pointerdown',
    'touchstart',
    'mousedown',
    'keydown',
    'wheel',
  ] as const
}
